import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Download,
  Printer,
  Mail,
  Share2,
  FileText,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface PDFViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfBlob: Blob | null;
  fileName: string;
  metadata?: {
    customerName?: string;
    offerNumber?: string;
    totalGross?: number;
    itemCount?: number;
    createdAt?: string;
  };
}

export function PDFViewerModal({
  isOpen,
  onClose,
  pdfBlob,
  fileName,
  metadata
}: PDFViewerModalProps) {
  const { toast } = useToast();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const viewerSrc = React.useMemo(() => {
    if (!pdfUrl) {
      return '';
    }
    return `${pdfUrl}#toolbar=1&navpanes=0&zoom=${zoom}`;
  }, [pdfUrl, zoom]);

  // Create object URL when blob changes
  React.useEffect(() => {
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    return undefined;
  }, [pdfBlob]);

  // Handle download
  const handleDownload = useCallback(() => {
    if (!pdfBlob) return;

    setIsDownloading(true);
    try {
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);

      setDownloadComplete(true);
      toast({
        title: 'PDF heruntergeladen',
        description: fileName,
      });
      setTimeout(() => setDownloadComplete(false), 3000);
    } finally {
      setIsDownloading(false);
    }
  }, [pdfBlob, fileName, toast]);

  // Handle print
  const handlePrint = useCallback(() => {
    if (!pdfUrl) return;

    setIsPrinting(true);
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        setIsPrinting(false);
      };
    } else {
      // Fallback: open in new tab
      window.open(pdfUrl, '_blank');
      setIsPrinting(false);
    }
  }, [pdfUrl]);

  // Handle email (placeholder - opens mailto with attachment hint)
  const handleEmail = useCallback(() => {
    const subject = encodeURIComponent(`Angebot ${metadata?.offerNumber || ''}`);
    const body = encodeURIComponent(
      `Sehr geehrte Damen und Herren,\n\nanbei erhalten Sie unser Angebot ${metadata?.offerNumber || ''}.\n\n` +
      `Kunde: ${metadata?.customerName || ''}\n` +
      `Gesamtsumme: ${metadata?.totalGross?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) || ''}\n\n` +
      `Mit freundlichen Grüßen`
    );

    // Note: mailto can't attach files directly, user needs to attach manually
    window.location.href = `mailto:?subject=${subject}&body=${body}`;

    toast({
      title: 'E-Mail wird vorbereitet',
      description: 'Bitte fügen Sie die PDF-Datei manuell als Anhang hinzu.',
    });
  }, [metadata, toast]);

  // Handle copy link (placeholder for future implementation)
  const handleShare = useCallback(() => {
    toast({
      title: 'Kundenportal kommt bald',
      description: 'Link-Versand wie bei Lexoffice wird in Kürze verfügbar sein.',
    });
  }, [toast]);

  // Zoom controls
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));
  if (!pdfBlob) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>PDF Vorschau: {fileName}</DialogTitle>
        </DialogHeader>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-blue-600" />
            <div>
              <h2 className="font-semibold text-sm">{fileName}</h2>
              {metadata?.customerName && (
                <p className="text-xs text-muted-foreground">{metadata.customerName}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 mr-2 border rounded-md px-1">
              <Button variant="ghost" size="sm" onClick={handleZoomOut} className="h-7 w-7 p-0">
                <ZoomOut className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs w-10 text-center">{zoom}%</span>
              <Button variant="ghost" size="sm" onClick={handleZoomIn} className="h-7 w-7 p-0">
                <ZoomIn className="h-3.5 w-3.5" />
              </Button>
            </div>

          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* PDF Preview */}
          <div className="flex-1 bg-gray-100 overflow-hidden p-2">
            {pdfUrl ? (
              <iframe
                src={viewerSrc}
                className="w-full h-full border-0 bg-white rounded-sm shadow-sm"
                title="PDF Preview"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-64 border-l bg-white flex flex-col flex-shrink-0">
            {/* Metadata Section */}
            {metadata && (
              <div className="p-4 border-b space-y-3">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Dokument-Info
                </h3>

                {metadata.offerNumber && (
                  <div>
                    <p className="text-xs text-muted-foreground">Angebotsnummer</p>
                    <p className="font-medium text-sm">{metadata.offerNumber}</p>
                  </div>
                )}

                {metadata.customerName && (
                  <div>
                    <p className="text-xs text-muted-foreground">Kunde</p>
                    <p className="font-medium text-sm">{metadata.customerName}</p>
                  </div>
                )}

                {metadata.totalGross !== undefined && (
                  <div>
                    <p className="text-xs text-muted-foreground">Gesamtsumme (brutto)</p>
                    <p className="font-bold text-lg text-green-700">
                      {metadata.totalGross.toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR'
                      })}
                    </p>
                  </div>
                )}

                {metadata.itemCount !== undefined && (
                  <div>
                    <p className="text-xs text-muted-foreground">Positionen</p>
                    <p className="font-medium text-sm">{metadata.itemCount}</p>
                  </div>
                )}

                {metadata.createdAt && (
                  <div>
                    <p className="text-xs text-muted-foreground">Erstellt am</p>
                    <p className="font-medium text-sm">
                      {new Date(metadata.createdAt).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Actions Section */}
            <div className="p-4 space-y-2 flex-1">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Aktionen
              </h3>

              <TooltipProvider>
                {/* Download Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleDownload}
                      className="w-full justify-start gap-2"
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : downloadComplete ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      {downloadComplete ? 'Heruntergeladen' : 'Herunterladen'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>PDF-Datei speichern</p>
                  </TooltipContent>
                </Tooltip>

                {/* Print Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handlePrint}
                      variant="outline"
                      className="w-full justify-start gap-2"
                      disabled={isPrinting}
                    >
                      {isPrinting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Printer className="h-4 w-4" />
                      )}
                      Drucken
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>Dokument drucken</p>
                  </TooltipContent>
                </Tooltip>

                <Separator className="my-3" />

                {/* Email Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleEmail}
                      variant="outline"
                      className="w-full justify-start gap-2"
                    >
                      <Mail className="h-4 w-4" />
                      Per E-Mail senden
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>E-Mail mit Angebot vorbereiten</p>
                  </TooltipContent>
                </Tooltip>

                {/* Share Link Button (Coming Soon) */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleShare}
                      variant="outline"
                      className="w-full justify-start gap-2 opacity-60"
                    >
                      <Share2 className="h-4 w-4" />
                      Link teilen
                      <Badge variant="secondary" className="ml-auto text-[10px]">Bald</Badge>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>Kundenportal-Link erstellen (kommt bald)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50">
              <p className="text-[10px] text-muted-foreground text-center">
                PDF-Vorschau wie bei Lexoffice
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PDFViewerModal;
